import { HISTORY } from 'constants/proptypes';
import React from 'react';
import PropTypes, { bool, string } from 'prop-types';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { createStructuredSelector } from 'reselect';

import Typography from 'ravenjs/lib/Typography';
import Row from 'ravenjs/lib/Row';
import Col from 'ravenjs/lib/Col';
import Divider from 'ravenjs/lib/Divider';
import { renderCheck } from 'ravenjs/utils/viewport';
import { get } from 'ravenjs/utils';

import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import NavigationBackIcon from 'components/Icons/NavigationBackIcon';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as errorSelectors } from 'modules/errors';
import { selectors as brandingSelectors } from 'modules/branding';

import LoginLogo from '../LoginLogo';

const AuthWrapperStyled = styled(Row)`
    max-width: 50rem;
    margin: auto;
    text-align: center;
    ${({ previewMode }) =>
        previewMode &&
        css`
            background-color: ${DEFAULT_COLORS.WHITE};
        `}
`;

const HeaderStyled = styled(Typography)`
    height: 3rem;
    font-family: Favorit;
    font-size: 2rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;
    color: ${DEFAULT_COLORS.BLACK};
    ${({ theme }) => theme.media.down('md')`
        font-size: 1.5rem;
    `};
`;

const BackButtonWrapper = styled(Col)`
    text-align: left;
    padding: 0;
    cursor: pointer;
`;

const BackButtonText = styled(Typography)`
    width: 3.9rem;
    height: 2rem;
    flex-grow: 0;
    margin: 0 0 0 0.56rem;
    font-family: Favorit;
    font-size: 1.5rem;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: ${DEFAULT_COLORS.BLACK};
`;

const DividerStyled = styled(Divider)`
    ${({ theme }) => theme.media.down('md')`
        max-width: 20rem;
    `};
    ${({ previewMode }) =>
        previewMode &&
        css`
            width: 80%;
        `}
    background-color: ${DEFAULT_COLORS.LIGHTLY};
`;

const ColStyled = styled(Col)`
    ${({ longHeader }) => css`
        text-align: ${longHeader ? 'left' : 'center'};
    `};
`;

const AuthWrapper = ({
    history,
    children,
    loginLogoUrl,
    header,
    showBack,
    previewMode,
    longHeader,
}) => {
    const isMobile = renderCheck('md', 'less');
    const handleGoBack = () => {
        const pathname = get(history, 'location.pathname', '');
        switch (pathname) {
            case '/auth/help-options':
                history.push('/auth/login');
                break;
            case '/auth/account-password-reset':
            case '/auth/forgot-username':
            case '/auth/tech-support':
                history.push('/auth/help-options');
                break;
            default:
                history.goBack();
                break;
        }
    };

    const renderBackButton = () => {
        return (
            !isMobile &&
            showBack && (
                <BackButtonWrapper onClick={handleGoBack} alignSelf="center" size={2}>
                    <NavigationBackIcon />
                    <BackButtonText as="span">{MESSAGES.LOGIN.BUTTONS.BACK}</BackButtonText>
                </BackButtonWrapper>
            )
        );
    };

    return (
        <AuthWrapperStyled justify="center" gutter={false} previewMode={previewMode}>
            <Col gutter={false} margin="3.75rem 0 2.5rem 0" size={12}>
                {loginLogoUrl && (
                    <>
                        <LoginLogo src={loginLogoUrl} alt="Logo" />
                        <DividerStyled margin="2rem auto" light previewMode={previewMode} />
                    </>
                )}
            </Col>
            {renderBackButton()}
            <ColStyled
                size={{ xs: 12, md: previewMode ? 12 : longHeader ? 10 : 8 }}
                rightOffset={{ md: 0, lg: longHeader ? 0 : 2 }}
                leftOffset={{ md: 0, lg: showBack ? 0 : 2 }}
                longHeader={longHeader}
            >
                <HeaderStyled as="span" gutterBottom="2.5rem" wordWrap>
                    {header}
                </HeaderStyled>
            </ColStyled>
            {children}
        </AuthWrapperStyled>
    );
};

AuthWrapper.propTypes = {
    children: PropTypes.node,
    loginLogoUrl: string,
    header: string,
    showBack: bool,
    history: HISTORY.isRequired,
    previewMode: bool,
    longHeader: bool,
};

AuthWrapper.defaultProps = {
    children: null,
    loginLogoUrl: '',
    header: '',
    showBack: false,
    previewMode: false,
    longHeader: false,
};

const mapStateToProps = createStructuredSelector({
    isLoggedIn: authSelectors.isUserLoggedIn,
    loginError: errorSelectors.getAuthErrors('login'),
    loginHeader: brandingSelectors.getLoginHeader,
    loginLogoUrl: brandingSelectors.getLoginLogoUrl,
});

export { AuthWrapper as AuthWrapperUnwrapped };
export default connect(mapStateToProps)(AuthWrapper);
